import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { CodeArtifactKind, CodeProject } from './types';
export interface ArtifactFilters {
  targetIds: Set<string>;
  kinds: Set<CodeArtifactKind>;
}
interface Props {
  isOpen: boolean;
  onClose: () => void;
  projects: CodeProject[];
  filters: ArtifactFilters;
  onApply: (filters: ArtifactFilters) => void;
}
const KINDS: {
  id: CodeArtifactKind;
  label: string;
}[] = [{
  id: 'pages-updated',
  label: 'Pages updated'
}, {
  id: 'test-results',
  label: 'Test results'
}, {
  id: 'hitl-step',
  label: 'Needs action'
}, {
  id: 'preview-url',
  label: 'Preview'
}];
export function ArtifactFilterSheet({
  isOpen,
  onClose,
  projects,
  filters,
  onApply
}: Props) {
  const [draft, setDraft] = useState<ArtifactFilters>({
    targetIds: new Set(filters.targetIds),
    kinds: new Set(filters.kinds)
  });
  React.useEffect(() => {
    if (isOpen) {
      setDraft({
        targetIds: new Set(filters.targetIds),
        kinds: new Set(filters.kinds)
      });
    }
  }, [isOpen, filters]);
  const toggleTarget = (id: string) => {
    const next = new Set(draft.targetIds);
    if (next.has(id)) next.delete(id);else next.add(id);
    setDraft({
      ...draft,
      targetIds: next
    });
  };
  const toggleKind = (kind: CodeArtifactKind) => {
    const next = new Set(draft.kinds);
    if (next.has(kind)) next.delete(kind);else next.add(kind);
    setDraft({
      ...draft,
      kinds: next
    });
  };
  const clear = () => setDraft({
    targetIds: new Set(),
    kinds: new Set()
  });
  return <AnimatePresence data-id="element-418">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 z-[80]" onClick={onClose} data-id="element-419" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 280
      }} className="fixed inset-x-0 bottom-0 z-[81] bg-[#141418] rounded-t-[20px] border-t border-white/[0.06]" style={{
        maxHeight: '60vh'
      }} data-id="element-420">
            <div className="flex justify-center pt-2 pb-1" data-id="element-421">
              <div className="w-10 h-1 rounded-full bg-[#8a8680]/30" data-id="element-422" />
            </div>
            <div className="px-5 py-3 border-b border-white/[0.04]" data-id="element-423">
              <div className="text-[14px] text-[#e8e4df]" data-id="element-424">Filter artifacts</div>
            </div>
            <div className="px-5 py-4 overflow-y-auto" style={{
          maxHeight: 'calc(60vh - 140px)'
        }} data-id="element-425">
              <div className="text-[11px] text-[#8a8680] uppercase tracking-wide mb-3" data-id="element-426">Project &rsaquo; Target</div>
              <div className="space-y-4 mb-6" data-id="element-427">
                {projects.map(project => <div key={project.id} data-id="element-428">
                    <div className="text-[13px] text-[#e8e4df] mb-2" data-id="element-429">{project.name}</div>
                    <div className="space-y-1.5 pl-2" data-id="element-430">
                      {project.targets.map(target => {
                  const checked = draft.targetIds.has(target.id);
                  return <button key={target.id} onClick={() => toggleTarget(target.id)} className="flex items-center gap-2.5 w-full text-left hover:text-[#e8e4df] transition-colors" data-id="element-431">
                            <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors ${checked ? 'bg-[#4a9a9a] border-[#4a9a9a]' : 'border-[#8a8680]/40'}`} data-id="element-432">
                              {checked && <Check size={11} className="text-[#141418]" strokeWidth={2.5} data-id="element-433" />}
                            </div>
                            <span className="text-[13px] text-[#8a8680]" data-id="element-434">{target.name}</span>
                          </button>;
                })}
                    </div>
                  </div>)}
              </div>

              <div className="text-[11px] text-[#8a8680] uppercase tracking-wide mb-3" data-id="element-435">Type</div>
              <div className="flex flex-wrap gap-2" data-id="element-436">
                {KINDS.map(k => {
              const active = draft.kinds.has(k.id);
              return <button key={k.id} onClick={() => toggleKind(k.id)} className={`px-3 py-1.5 rounded-full text-[12px] transition-colors ${active ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-white/[0.04] text-[#8a8680]'}`} data-id="element-437">
                      {k.label}
                    </button>;
            })}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/[0.04] flex items-center gap-3" data-id="element-438">
              <button onClick={clear} className="text-[13px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-439">
                Clear
              </button>
              <div className="flex-1" data-id="element-440" />
              <button onClick={onClose} className="px-4 py-2 text-[13px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-441">
                Cancel
              </button>
              <button onClick={() => {
            onApply(draft);
            onClose();
          }} className="px-4 py-2 text-[13px] rounded-full bg-[#4a9a9a]/20 text-[#4a9a9a] hover:bg-[#4a9a9a]/30 transition-colors" data-id="element-442">
                Apply
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}